import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UtensilsCrossed, MapPin, Phone, Clock, ArrowRight, Search, Store, Star, Award, Sparkles, Navigation, X, ChevronLeft, ChevronRight, Wheat, PawPrint } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import useDescTranslate from "../lib/useDescTranslate";
import InstallAppButtons from "../components/InstallAppButtons";
import { useAuth } from "../contexts/AuthContext";
import { loadProvinces, getProvinceForCity } from "../lib/italyProvinces";

const HERO_IMG = "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZGFyayUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvciUyMGV2ZW5pbmclMjBsdXh1cnl8ZW58MHx8fHwxNzc2OTY1MTgzfDA&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "/logo-prenova.png";

export default function Landing() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [restaurants, setRestaurants] = useState([]);
    const [cities, setCities] = useState([]);
    const [province, setProvince] = useState("");
    const [provData, setProvData] = useState(null);  // { provinceToCities, cityToProvince }
    const [query, setQuery] = useState("");
    const [filterGlutenFree, setFilterGlutenFree] = useState(false);
    const [filterPetsAllowed, setFilterPetsAllowed] = useState(false);
    const [city, setCity] = useState("");
    const [geo, setGeo] = useState(null); // {lat, lng}
    const [radiusKm, setRadiusKm] = useState(25);
    const [locating, setLocating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [points, setPoints] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 6;

    const loadRestaurants = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (city) params.city = city;
            if (geo) {
                // Passiamo SOLO lat/lng (senza radius_km): il backend usa la
                // posizione per calcolare distance_km e ordinare i ristoranti
                // dai più vicini ai più lontani, restituendoli TUTTI (nessun
                // cutoff per raggio). Così il cliente vede l'intero catalogo
                // ordinato sensatamente in base alla sua posizione.
                params.lat = geo.lat;
                params.lng = geo.lng;
            }
            const r = await api.get("/restaurants", { params });
            setRestaurants(r.data);
        } catch {
            setRestaurants([]);
        } finally {
            setLoading(false);
        }
    }, [city, geo]);

    useEffect(() => { loadRestaurants(); }, [loadRestaurants]);

    useEffect(() => {
        api.get("/restaurants/cities").then((r) => setCities(r.data)).catch(() => setCities([]));
        loadProvinces().then(setProvData).catch(() => setProvData(null));
    }, []);

    // Tenta una geolocalizzazione "soft" all'apertura della home: se il browser
    // ha già il permesso concesso (decisione presa in una visita precedente),
    // recupera subito la posizione e ordina i ristoranti dai più vicini. Se il
    // permesso è "prompt" mostra il dialog nativo; se è "denied" non fa nulla.
    // Questo evita di rompere la UX dei visitatori che non vogliono condividere
    // la posizione, ma offre subito il sort per distanza a chi l'aveva già
    // accettata.
    useEffect(() => {
        if (!navigator.geolocation || geo || city) return;
        let cancelled = false;
        const tryLocate = () => {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    if (cancelled) return;
                    setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                },
                () => {},  // silent fail — l'utente potrà sempre cliccare il bottone
                { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 },
            );
        };
        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: "geolocation" })
                .then((status) => {
                    if (cancelled) return;
                    if (status.state === "granted") tryLocate();
                    else if (status.state === "prompt") tryLocate();
                    // "denied" → non chiediamo nulla
                })
                .catch(() => tryLocate());  // browser senza Permissions API
        } else {
            tryLocate();
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (user) {
            api.get("/me/points").then((r) => setPoints(r.data)).catch(() => setPoints(null));
        } else {
            setPoints(null);
        }
    }, [user]);

    const enableGeo = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocalizzazione non supportata dal browser");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setCity(""); // geo and city are mutually exclusive
                setLocating(false);
                toast.success("Posizione acquisita · ristoranti ordinati per distanza");
            },
            (err) => {
                setLocating(false);
                toast.error(err.code === 1 ? "Permesso negato. Abilita la posizione." : "Impossibile ottenere la posizione.");
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
    };

    const clearGeo = () => { setGeo(null); };

    const filtered = useMemo(() => {
        const out = restaurants.filter((r) => {
            if (filterGlutenFree && !r.gluten_free) return false;
            if (filterPetsAllowed && !r.pets_allowed) return false;
            if (province && getProvinceForCity(r.city, provData?.cityToProvince) !== province) return false;
            if (!query) return true;
            const q = query.toLowerCase();
            return r.name.toLowerCase().includes(q) || (r.address || "").toLowerCase().includes(q) || (r.city || "").toLowerCase().includes(q);
        });
        // Quando l'utente ha condiviso la posizione, ordina i ristoranti per
        // distanza crescente (più vicini in cima). I record senza distance_km
        // vanno in fondo. Senza geolocalizzazione, mantiene l'ordine del backend.
        if (geo) {
            out.sort((a, b) => {
                const da = a.distance_km == null ? Number.POSITIVE_INFINITY : a.distance_km;
                const db = b.distance_km == null ? Number.POSITIVE_INFINITY : b.distance_km;
                return da - db;
            });
        }
        return out;
    }, [restaurants, query, filterGlutenFree, filterPetsAllowed, geo, province, provData]);

    // Province italiane disponibili (tutte le 107, ordinate alfabeticamente).
    const provincesAvailable = useMemo(() => {
        if (!provData) return [];
        return Object.keys(provData.provinceToCities).sort((a, b) => a.localeCompare(b, "it"));
    }, [provData]);

    // Comuni (città) della provincia selezionata. Se nessuna provincia è
    // selezionata, mostra solo le città presenti nel DB (più sensato per
    // l'utente che non vuole esplorare l'intera Italia).
    const citiesForProvince = useMemo(() => {
        if (!province || !provData) return cities;
        return provData.provinceToCities[province] || [];
    }, [province, provData, cities]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // Translate restaurant descriptions dynamically when language ≠ Italian.
    const descriptionsToTranslate = useMemo(
        () => paginated.map((r) => r.description).filter(Boolean),
        [paginated],
    );
    const { tx: txDesc } = useDescTranslate(descriptionsToTranslate);

    // Reset page on filter/query change
    useEffect(() => { setPage(1); }, [city, province, geo, query, filterGlutenFree, filterPetsAllowed]);

    return (
        <div className="min-h-screen bg-ink" data-testid="landing-page">
            {/* HERO portale prenotazione */}
            <section className="relative min-h-[85vh] flex items-center overflow-hidden grain">
                <div className="absolute inset-0 z-0">
                    <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/65 to-black/90" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-6">
                            <div className="w-72 sm:w-80 md:w-96 h-32 md:h-36 flex items-center justify-start shrink-0 drop-shadow-[0_0_30px_rgba(212,175,55,0.25)]" data-testid="hero-logo">
                                <img src={LOGO_URL} alt="Prenova by Renova360" className="max-w-full max-h-full object-contain object-left" />
                            </div>
                            <div className="space-y-2.5" data-testid="hero-install-section">
                                <div className="text-[10px] uppercase tracking-[0.4em] text-gold/80 font-semibold">{t("install.section")}</div>
                                <InstallAppButtons size="sm" />
                            </div>
                        </div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-12 bg-gold" />
                            <span className="text-gold text-xs tracking-[0.4em] uppercase">{t("landing.hero_eyebrow")}</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.05] tracking-tight">
                            {t("landing.hero_title_1")}<br />
                            <span className="italic text-gold">{t("landing.hero_title_2")}</span>.<br />
                            {t("landing.hero_title_3")}
                        </h1>
                        <p className="mt-8 text-lg text-gray-300 max-w-xl font-light leading-relaxed">
                            {t("landing.hero_subtitle")}
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl">
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gold" size={18} strokeWidth={1.5} />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("landing.search_placeholder")}
                                    data-testid="search-input"
                                    className="w-full pl-12 pr-4 py-4 bg-black/40 backdrop-blur border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none"
                                />
                            </div>
                            <a href="#ristoranti"
                                className="inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold px-8 py-4 rounded-sm hover:bg-gold-light transition-colors tracking-wide whitespace-nowrap"
                                data-testid="hero-explore-btn">
                                {t("common.discover")} <ArrowRight size={16} />
                            </a>
                        </div>

                        <div className="mt-8 inline-flex items-center gap-3 text-xs text-gray-300/90 bg-white/5 border border-gold/20 px-4 py-2.5 rounded-sm backdrop-blur-sm">
                            <Store size={14} className="text-gold" strokeWidth={1.5} />
                            <span>{t("landing.are_you_restaurateur")}</span>
                            <Link to="/iscrizione-ristoratore"
                                className="text-gold hover:text-gold-light underline underline-offset-2 tracking-wide font-semibold"
                                data-testid="hero-signup-restaurateur">
                                {t("landing.iscriviti_renova")}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* LOYALTY REMINDER (only for logged-in customers) */}
            {user && points && (
                <section className="px-6 md:px-10 -mt-10 relative z-20" data-testid="loyalty-banner">
                    <div className="max-w-7xl mx-auto">
                        <div className="bg-gradient-to-r from-gold/20 via-gold/10 to-transparent border border-gold/40 backdrop-blur rounded-sm p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5">
                            <div className="w-14 h-14 rounded-sm bg-gold/30 flex items-center justify-center flex-shrink-0">
                                <Award size={28} className="text-gold" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">{t("loyalty.hello")} {user.name?.split(" ")[0] || t("loyalty.default_name")}</div>
                                {points.pending_reviews && points.pending_reviews.length > 0 ? (
                                    <>
                                        <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight">
                                            {t("loyalty.pending_reviews", { count: points.pending_reviews.length })}
                                        </h3>
                                        <p
                                            className="text-sm text-gray-300 mt-2 max-w-2xl"
                                            dangerouslySetInnerHTML={{ __html: t("loyalty.pending_reviews_hint", { points: points.points }) }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <h3
                                            className="font-serif text-2xl md:text-3xl text-white leading-tight"
                                            dangerouslySetInnerHTML={{ __html: t("loyalty.your_points", { count: points.points }).replace(/<gold>/g, '<span class="text-gold">').replace(/<\/gold>/g, '</span>') }}
                                        />
                                        <p
                                            className="text-sm text-gray-300 mt-2 max-w-2xl"
                                            dangerouslySetInnerHTML={{ __html: t("loyalty.earn_points_hint", { per_review: points.points_per_review }).replace(/<strong>/g, '<strong class="text-gold">') }}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <Link to="/my-reservations"
                                    className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light text-sm tracking-wide whitespace-nowrap"
                                    data-testid="loyalty-cta">
                                    <Sparkles size={14} /> {t("loyalty.go_to_visits")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* DIRECTORY */}
            <section id="ristoranti" className="py-24 px-6 md:px-10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-px w-10 bg-gold" />
                                <span className="text-gold text-xs tracking-[0.4em] uppercase">{t("landing.directory_eyebrow")}</span>
                            </div>
                            <h2 className="font-serif text-4xl lg:text-5xl text-white">
                                {t("landing.directory_title_1")} <span className="italic text-gold">{t("landing.directory_title_2")}</span>
                            </h2>
                        </div>
                        <p className="text-gray-400 text-sm" data-testid="results-count">
                            {loading ? t("common.loading") : t("landing.results_count", { count: filtered.length })}
                        </p>
                    </div>

                    {/* FILTER BAR */}
                    <div className="bg-ink-surface border border-white/10 rounded-sm p-5 mb-8 flex flex-wrap items-end gap-4" data-testid="filter-bar">
                        <div className="flex-1 min-w-[180px]">
                            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">Provincia</label>
                            <select value={province}
                                onChange={(e) => {
                                    const newProv = e.target.value;
                                    setProvince(newProv);
                                    // Reset city if it doesn't belong to the new province
                                    if (newProv && city && getProvinceForCity(city, provData?.cityToProvince) !== newProv) {
                                        setCity("");
                                    }
                                    // Disabilita la geolocalizzazione quando si filtra per area
                                    if (newProv) setGeo(null);
                                }}
                                data-testid="province-filter"
                                className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                                <option value="">Tutte le province</option>
                                {provincesAvailable.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[180px]">
                            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">{t("landing.city_label")}</label>
                            <select value={city}
                                onChange={(e) => {
                                    setCity(e.target.value);
                                    if (e.target.value) setGeo(null);
                                }}
                                data-testid="city-filter"
                                className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                                <option value="">{province ? "Tutte le città della provincia" : t("landing.all_cities")}</option>
                                {citiesForProvince.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        <div className="flex-1 min-w-[220px]">
                            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">
                                {t("landing.near_me")}
                            </label>
                            <div className="flex items-center gap-2">
                                {!geo ? (
                                    <button onClick={enableGeo} disabled={locating}
                                        className="flex items-center gap-2 px-4 py-2.5 bg-gold/10 border border-gold/40 text-gold rounded-sm hover:bg-gold/20 text-sm uppercase tracking-widest disabled:opacity-50"
                                        data-testid="geo-enable-btn">
                                        <Navigation size={14} strokeWidth={1.5} />
                                        {locating ? t("landing.locating") : t("landing.find_nearby")}
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="flex-1 flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 border border-emerald-400/40 text-emerald-300 rounded-sm text-xs uppercase tracking-widest" data-testid="geo-active-badge">
                                            <Navigation size={13} strokeWidth={1.8} />
                                            <span>Ordinati per distanza</span>
                                        </div>
                                        <button onClick={clearGeo}
                                            className="p-2 text-gray-400 hover:text-red-400 border border-white/10 rounded-sm"
                                            data-testid="geo-clear-btn" title={t("landing.remove_geo_filter")}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-1 min-w-[180px]">
                            <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-2">{t("landing.search_by_name")}</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" size={14} strokeWidth={1.5} />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("landing.search_by_name_placeholder")}
                                    data-testid="directory-search-input"
                                    className="w-full pl-9 pr-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none text-sm"
                                />
                            </div>
                        </div>

                        {(city || geo || query || filterGlutenFree || filterPetsAllowed) && (
                            <button
                                onClick={() => { setCity(""); setGeo(null); setQuery(""); setFilterGlutenFree(false); setFilterPetsAllowed(false); }}
                                className="text-xs text-gray-400 hover:text-gold uppercase tracking-widest px-3 py-2.5 self-end"
                                data-testid="clear-filters-btn">
                                {t("landing.clear_filters")}
                            </button>
                        )}
                    </div>

                    {/* Quick toggles: gluten-free / pets */}
                    <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="quick-toggles">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mr-1">{t("landing.quick_filters")}</span>
                        <button
                            onClick={() => setFilterGlutenFree((v) => !v)}
                            data-testid="filter-gluten-free"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-[11px] font-semibold tracking-widest uppercase transition-colors ${
                                filterGlutenFree
                                    ? "bg-emerald-500 text-black border-emerald-500"
                                    : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            }`}
                        >
                            <Wheat size={11} strokeWidth={2} /> {t("landing.filter_gluten_free")}
                        </button>
                        <button
                            onClick={() => setFilterPetsAllowed((v) => !v)}
                            data-testid="filter-pets-allowed"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-[11px] font-semibold tracking-widest uppercase transition-colors ${
                                filterPetsAllowed
                                    ? "bg-emerald-500 text-black border-emerald-500"
                                    : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            }`}
                        >
                            <PawPrint size={11} strokeWidth={2} /> {t("landing.filter_pets_allowed")}
                        </button>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-16 text-center">
                            <UtensilsCrossed size={40} className="text-gray-600 mx-auto mb-4" strokeWidth={1} />
                            <p className="text-gray-400 font-serif text-xl">{t("landing.no_results")}</p>
                            <p className="text-gray-500 text-sm mt-2">{t("landing.no_results_hint")}</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {paginated.map((r) => (
                                    <Link key={r.restaurant_id} to={`/r/${r.slug}`} data-testid={`directory-${r.slug}`}
                                        className="group bg-ink-surface border border-white/10 rounded-sm p-7 hover:border-gold/60 transition-all hover:-translate-y-1">
                                        <div className="flex items-start justify-between mb-4">
                                            {r.logo_path ? (
                                                <div className="w-12 h-12 rounded-sm overflow-hidden border border-gold/30 bg-ink shrink-0" data-testid={`directory-logo-${r.slug}`}>
                                                    <img src={`${API}/files/${r.logo_path}`} alt={r.name} className="w-full h-full object-cover" />
                                                </div>
                                            ) : (
                                                <div className="w-12 h-12 bg-gold/10 border border-gold/30 rounded-sm flex items-center justify-center">
                                                    <UtensilsCrossed size={20} className="text-gold" strokeWidth={1.5} />
                                                </div>
                                            )}
                                            <ArrowRight size={18} className="text-gray-600 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                                        </div>
                                        <h3 className="font-serif text-2xl text-white group-hover:text-gold transition-colors">{r.name}</h3>
                                        {r.gluten_free && (
                                            <div className="mt-1.5 inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 px-2 py-0.5 rounded-sm text-[10px] font-semibold tracking-widest uppercase" data-testid={`directory-gluten-${r.slug}`}>
                                                <Wheat size={10} strokeWidth={2} /> {t("landing.filter_gluten_free")}
                                            </div>
                                        )}
                                        {r.distance_km != null && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold" data-testid={`distance-${r.slug}`}>
                                                <Navigation size={10} strokeWidth={1.8} /> {r.distance_km} km
                                            </div>
                                        )}
                                        {r.city && (
                                            <div className="text-[11px] text-gray-500 mt-1">{r.city}</div>
                                        )}

                                        {/* Stars + recensioni */}
                                        <div className="flex items-center gap-3 mt-3 flex-wrap" data-testid={`directory-rating-${r.slug}`}>
                                            <div className="flex gap-0.5">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star key={n} size={13}
                                                        className={n <= Math.round(r.avg_rating || 0) ? "text-gold fill-gold" : "text-white/15"}
                                                        strokeWidth={1.5} />
                                                ))}
                                            </div>
                                            {r.review_count > 0 ? (
                                                <>
                                                    <span className="text-xs text-white">
                                                        <strong>{(r.avg_rating || 0).toFixed(1)}</strong>
                                                        <span className="text-gray-500 ml-1">({r.review_count})</span>
                                                    </span>
                                                    <Link
                                                        to={`/r/${r.slug}#reviews`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-[10px] uppercase tracking-widest text-gold hover:text-gold-light underline underline-offset-2"
                                                        data-testid={`read-reviews-${r.slug}`}>
                                                        {t("restaurant_home.read_reviews")}
                                                    </Link>
                                                </>
                                            ) : (
                                                <span className="text-[10px] uppercase tracking-widest text-gray-500">{t("landing.no_reviews_yet")}</span>
                                            )}
                                        </div>

                                        {r.address && <div className="flex items-start gap-2 text-xs text-gray-500 mt-3"><MapPin size={12} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} /><span>{r.address}</span></div>}
                                        {r.opening_hours && <div className="flex items-start gap-2 text-xs text-gray-500 mt-2"><Clock size={12} className="mt-0.5 flex-shrink-0" strokeWidth={1.5} /><span>{r.opening_hours}</span></div>}
                                        {r.description && <p className="text-sm text-gray-400 mt-4 leading-relaxed line-clamp-2" data-testid={`directory-desc-${r.slug}`}>{txDesc(r.description)}</p>}
                                    </Link>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
                                    <button
                                        onClick={() => {
                                            setPage((p) => Math.max(1, p - 1));
                                            document.getElementById("ristoranti")?.scrollIntoView({ behavior: "smooth" });
                                        }}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center gap-1 px-4 py-2 border border-white/20 rounded-sm text-gray-300 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
                                        data-testid="page-prev-btn">
                                        <ChevronLeft size={14} /> Prec
                                    </button>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                                        .reduce((acc, n, idx, arr) => {
                                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…");
                                            acc.push(n);
                                            return acc;
                                        }, [])
                                        .map((n, i) => (
                                            n === "…" ? (
                                                <span key={`gap-${i}`} className="px-2 text-gray-500">…</span>
                                            ) : (
                                                <button key={n}
                                                    onClick={() => {
                                                        setPage(n);
                                                        document.getElementById("ristoranti")?.scrollIntoView({ behavior: "smooth" });
                                                    }}
                                                    data-testid={`page-${n}-btn`}
                                                    className={`min-w-[40px] px-3 py-2 border rounded-sm text-sm ${
                                                        currentPage === n
                                                            ? "border-gold bg-gold text-black font-semibold"
                                                            : "border-white/20 text-gray-300 hover:border-gold hover:text-gold"}`}>
                                                    {n}
                                                </button>
                                            )
                                        ))}

                                    <button
                                        onClick={() => {
                                            setPage((p) => Math.min(totalPages, p + 1));
                                            document.getElementById("ristoranti")?.scrollIntoView({ behavior: "smooth" });
                                        }}
                                        disabled={currentPage === totalPages}
                                        className="inline-flex items-center gap-1 px-4 py-2 border border-white/20 rounded-sm text-gray-300 hover:border-gold hover:text-gold disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-widest text-xs"
                                        data-testid="page-next-btn">
                                        Succ <ChevronRight size={14} />
                                    </button>
                                </div>
                            )}

                            <p className="text-center text-[11px] uppercase tracking-[0.3em] text-gray-500 mt-4" data-testid="pagination-info">
                                Pagina {currentPage} di {totalPages} · {filtered.length} risultati
                            </p>
                        </>
                    )}
                </div>
            </section>

            <footer className="py-12 px-6 md:px-10 border-t border-white/10 text-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-44 h-20 flex items-center justify-center" data-testid="footer-logo">
                        <img src={LOGO_URL} alt="Prenova by Renova360" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex gap-6 text-xs text-gray-500 tracking-widest uppercase flex-wrap justify-center">
                        <span>Prenova by Renova360</span>
                        <a href="/admin/login" className="hover:text-gold" data-testid="footer-admin-link">Area Ristoratori</a>
                        <a href="/adminrenova/login" className="hover:text-gold" data-testid="footer-superadmin-link">Accesso Admin</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
